import React, { FC } from 'react';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Radio,
  Tab,
  Typography,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  CustomFormulaValues,
  NumberType,
} from 'src/components/Forms/CustomFormulaForm';
import {
  InputsContainer,
  LoaderContainer,
  RadioButtonLabel,
  RadioButtonsContainer,
  TabContent,
  Tabs,
  Title,
  Wrapper,
} from 'src/components/Forms/CustomFormulaForm/Formatting/styled';
import { TextInput } from 'src/components/Forms/FormInputs/TextInput';
import { MetricFormatType } from 'src/utils/gql/types';

export const Formatting: FC<{ loading: boolean }> = ({ loading }) => {
  const { setValue, control } = useFormContext<CustomFormulaValues>();
  const tabValue = useWatch({
    control,
    name: 'format.type',
  });

  const handleChangeTab = (
    _: React.SyntheticEvent,
    newValue: MetricFormatType,
  ) => {
    setValue('format.type', newValue);
  };

  if (loading || !tabValue) {
    return (
      <LoaderContainer>
        <CircularProgress size={40} />
      </LoaderContainer>
    );
  }

  return (
    <Wrapper>
      <Title>Format</Title>
      <Tabs
        value={tabValue}
        onChange={handleChangeTab}
        TabIndicatorProps={{ hidden: true }}
      >
        <Tab label="Number" value={MetricFormatType.Number} />
        <Tab label="Percent" value={MetricFormatType.Percent} />
        <Tab label="Currency" value={MetricFormatType.Currency} />
        <Tab label="Duration" value={MetricFormatType.Duration} />
      </Tabs>
      <TabContent>
        <Box>
          <Title>Description</Title>
          <TextInput
            multiline
            placeholder="Explain what this metric is used for..."
            name="format.description"
            type="text"
          />
        </Box>
        {/* Mostra i controlli specifici solo per il tipo Number */}
        {tabValue === MetricFormatType.Number && (
          <>
            <InputsContainer>
              <Box>
                <Title>Prefix</Title>
                <TextInput
                  placeholder="E.g. #"
                  name="format.prefix"
                  type="text"
                />
              </Box>
              <Box>
                <Title>Suffix</Title>
                <TextInput
                  placeholder="E.g. k"
                  name="format.suffix"
                  type="text"
                />
              </Box>
            </InputsContainer>
            <Controller
              control={control}
              name="format.numberType"
              render={({ field: { onChange, value } }) => (
                <RadioButtonsContainer>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={value === NumberType.WholeNumber}
                        onChange={onChange}
                      />
                    }
                    label={
                      <RadioButtonLabel>
                        <Typography>Whole number</Typography>
                        <Typography variant="body2">E.g. 5 or -24</Typography>
                      </RadioButtonLabel>
                    }
                    value={NumberType.WholeNumber}
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={value === NumberType.Decimal}
                        onChange={onChange}
                      />
                    }
                    label={
                      <RadioButtonLabel>
                        <Typography>Decimal</Typography>
                        <Typography variant="body2">
                          E.g. 5.34 or -23.70
                        </Typography>
                      </RadioButtonLabel>
                    }
                    value={NumberType.Decimal}
                  />
                </RadioButtonsContainer>
              )}
            />
          </>
        )}
        {/* Mostra informazioni specifiche per altri formati */}
        {tabValue === MetricFormatType.Percent && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Values will be displayed as percentages (e.g. 25.5%)
            </Typography>
          </Box>
        )}
        {tabValue === MetricFormatType.Currency && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Values will be displayed as currency (e.g. $1,234.56)
            </Typography>
          </Box>
        )}
        {tabValue === MetricFormatType.Duration && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Values will be displayed as duration (e.g. 2h 30m)
            </Typography>
          </Box>
        )}
      </TabContent>
    </Wrapper>
  );
};
